import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";
import { TIPO_PERSONA_LABELS, summarizeCategorie } from "@/lib/anagrafiche";

const labelOf = (a) => a.ragione_sociale || `${a.cognome || ""} ${a.nome || ""}`.trim() || "(senza nome)";

export default function AnagraficaCestino() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/anagrafiche/cestino");
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onRipristina = async (a) => {
    const ok = await confirm({
      title: "Ripristina anagrafica",
      message: `Vuoi ripristinare ${a.codice} (${labelOf(a)})?`,
      variant: "approve", confirmLabel: "SI, ripristina",
    });
    if (!ok) return;
    try {
      await api.post(`/anagrafiche/${a.id}/ripristina`);
      toast.success("Anagrafica ripristinata");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-5" data-testid="anag-cestino-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/anagrafiche")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Torna alle anagrafiche
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Cestino anagrafiche</h1>
          <p className="text-slate-500 text-sm">Anagrafiche soft-deleted.</p>
        </div>
      </div>
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Trash2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-700 font-medium">Cestino vuoto</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="p-2">Codice</th><th className="p-2">Nome</th><th className="p-2">Tipo</th>
                  <th className="p-2">Categorie</th><th className="p-2">Eliminato il</th>
                  <th className="p-2">Motivo</th><th className="p-2 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const sum = summarizeCategorie(a.categorie);
                  return (
                    <tr key={a.id} className="border-b hover:bg-slate-50/60">
                      <td className="p-2 font-mono text-xs text-blue-700">{a.codice}</td>
                      <td className="p-2">{labelOf(a)}</td>
                      <td className="p-2">{TIPO_PERSONA_LABELS[a.tipo_persona] || a.tipo_persona}</td>
                      <td className="p-2 text-xs">{sum.slice(0, 3).map((c) => c.label).join(", ")}{sum.length > 3 ? "..." : ""}</td>
                      <td className="p-2 text-xs text-slate-500">{(a.deleted_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-2 text-xs">{a.delete_reason || "—"}</td>
                      <td className="p-2 text-right">
                        <Button variant="outline" size="sm" onClick={() => onRipristina(a)} data-testid={`anag-restore-${a.codice}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Ripristina
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
